import { setAuthenticated } from '@frontend/core/lib/modules/authentication/slice'
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError
} from '@reduxjs/toolkit/query/react'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { toast } from 'sonner'
import { setSyncTime } from '../../domains/sync_status/slice'
import { RootState } from '../../store'
import { ServerKeys, serverMap } from './types'

const rawBaseQuery = fetchBaseQuery({
  baseUrl: '',
  credentials: 'include'
})

function trimSlashes(str: string): string {
  return str.replace(/^\/+|\/+$/g, '')
}

const generateDynamicBaseQuery = (key: ServerKeys) => {
  const dynamicBaseQuery: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    {
      isListWithEmpty404Response?: boolean
      suppressErrorToast?: boolean
    }
  > = async (args, api, extraOptions) => {
    const state = api.getState() as RootState
    const selectedRegionId = state.regions.selectedId

    const serverUrl = state.servers.urls[key]?.url

    const server = serverMap[key]

    // If the app is pitch side and the metrics server connection is down, return an error
    if (
      state.app.config.pitchSide &&
      !state.servers.connections.metricsServer.connected
    ) {
      if (!extraOptions?.suppressErrorToast)
        toast.error(`Service Unavailable: Metrics server is not connected`)

      return {
        error: {
          status: 503,
          statusText: 'Service Unavailable',
          data: 'Metrics server is not connected'
        }
      }
    }

    // gracefully handle scenarios where data to generate the URL is missing
    if (!serverUrl || !selectedRegionId) {
      return {
        error: {
          status: 400,
          statusText: 'Bad Request',
          data: 'No server url set'
        }
      }
    }

    const urlEnd = typeof args === 'string' ? args : args.url

    // construct a dynamically generated portion of the url
    // Ensure there's a trailing slash on the server URL and no leading slash on the URL end
    const resolvedUrl = serverUrl.replace('{region}', selectedRegionId)
    const baseUrl = trimSlashes(resolvedUrl)
    const formattedUrlEnd = trimSlashes(urlEnd)
    let adjustedUrl = baseUrl
    if (formattedUrlEnd === 'null') {
      throw new Error(`incoming arg was "null"`)
    }
    if (formattedUrlEnd === 'undefined') {
      throw new Error(`incoming arg was "undefined"`)
    }
    if (formattedUrlEnd && formattedUrlEnd !== '/') {
      adjustedUrl += '/' + formattedUrlEnd
    }

    console.debug('baseUrl', baseUrl)
    console.debug('adjustedUrl', adjustedUrl)
    console.debug('formattedUrlEnd', formattedUrlEnd)

    // if args is a string, we need to convert it to an object
    const adjustedArgs =
      typeof args === 'string'
        ? { url: adjustedUrl }
        : { ...args, url: adjustedUrl }

    let result = await rawBaseQuery(adjustedArgs, api, extraOptions)

    if (
      !state.app.config.exemptFromAuthentication &&
      result.error?.status === 401
    ) {
      console.debug('401 means we should get the token again')
      const authArgs = 'https://auth.sportable.com/api/token/refresh'
      const response = await rawBaseQuery(authArgs, api, extraOptions)
      if (response.error?.status === 401) {
        api.dispatch(setAuthenticated(false))
      } else {
        api.dispatch(setAuthenticated(true))
        result = await rawBaseQuery(adjustedArgs, api, extraOptions)
      }
    }

    if (result.error) {
      const errorData = result.error.data as { errorDetail?: string }

      // don't show 404 errors as toasts for now, they're often expected
      if (result.error.status === 404) {
        // if it's a 404 error for a list endpoint, return an empty list instead
        if (extraOptions?.isListWithEmpty404Response) {
          return {
            data: []
          }
        }

        return result
      }

      // don't show toast for authentication errors, they are handled globally
      if (result.error.status === 401) return result

      // callers that own their own error handling opt out of the generic toast
      if (extraOptions?.suppressErrorToast) return result

      // Log the full error to the console so callers can distinguish between
      // network failures (no status), CORS/DNS issues (FETCH_ERROR), and
      // backend errors. The toast message is deliberately short.
      console.error(`[api:${server.name}] ${urlEnd} failed`, {
        status: result.error.status,
        data: result.error.data,
        resolvedUrl: adjustedUrl,
        selectedRegionId
      })
      toast.error(
        `API error for ${server.name} at ${urlEnd} (${result.error.status}): ${
          errorData?.errorDetail || 'Unknown error'
        }`
      )
    }

    // Extract sync time from response header
    const syncTime = result.meta?.response?.headers?.get('X-Last-Sync-Time')
    if (syncTime) {
      const segments = formattedUrlEnd.split('/')
      const resource = segments[segments.length - 1] || formattedUrlEnd
      api.dispatch(setSyncTime({ resource, time: syncTime }))
    }

    return result
  }
  return dynamicBaseQuery
}

const dynamicServerBaseQueries = Object.keys(serverMap).reduce(
  (acc, key) => {
    acc[key as ServerKeys] = generateDynamicBaseQuery(key as ServerKeys)
    return acc
  },
  {} as Record<
    ServerKeys,
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>
  >
)

export const metricsServerApi = createApi({
  reducerPath: 'metricsServerApi',
  baseQuery: dynamicServerBaseQueries.metricsServer,
  tagTypes: [
    'offlineModeStatus',
    'pitches',
    'drills',
    'events',
    'hardware',
    'people',
    'sessions',
    'activeSession',
    'teampeople',
    'teampeoplesessions',
    'drillPlayerBreakdown',
    'enrollmentStatus',
    'broadcastState',
    'penaltyCountdown',
    'matchState',
    'geniusRoster'
  ],
  endpoints: (builder) => ({})
})

export const sportscasterApi = createApi({
  reducerPath: 'sportscasterApi',
  baseQuery: dynamicServerBaseQueries.sportscaster,
  tagTypes: [],
  endpoints(build) {
    return {}
  }
})

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: dynamicServerBaseQueries.auth,
  tagTypes: ['authToken'],
  endpoints(build) {
    return {}
  }
})

export const managementApi = createApi({
  reducerPath: 'managementApi',
  baseQuery: dynamicServerBaseQueries.managementApi,
  tagTypes: [
    'organisations',
    'users',
    'laptops',
    'adminOrganisations',
    'unlinkedTeams'
  ],
  endpoints(build) {
    return {}
  }
})

export const daemon = createApi({
  reducerPath: 'daemon',
  baseQuery: dynamicServerBaseQueries.daemon,
  tagTypes: [],
  endpoints(build) {
    return {}
  }
})

export const matchTrackerApi = createApi({
  reducerPath: 'matchTrackerApi',
  baseQuery: dynamicServerBaseQueries.matchTrackerApi,
  tagTypes: [
    'teams',
    'pitches',
    'uploadStatus',
    'people',
    'teampeople',
    'events',
    'sessionEvents',
    'drillPlayerBreakdown',
    'sessionPlayerBreakdown'
  ],
  endpoints(build) {
    return {}
  }
})

export const staticAssetApi = createApi({
  reducerPath: 'staticAssetApi',
  baseQuery: dynamicServerBaseQueries.staticAssetApi,
  tagTypes: [],
  endpoints(build) {
    return {}
  }
})

export const broadcastApi = createApi({
  reducerPath: 'broadcastApi',
  baseQuery: dynamicServerBaseQueries.broadcastApi,
  tagTypes: ['sessionEventsCSV'],
  endpoints(build) {
    return {}
  }
})

export const streamingApi = createApi({
  reducerPath: 'streamingApi',
  baseQuery: dynamicServerBaseQueries.streaming,
  tagTypes: [],
  endpoints(build) {
    return {}
  }
})

export const reportingApi = createApi({
  reducerPath: 'reportingApi',
  baseQuery: dynamicServerBaseQueries.reportingApi,
  tagTypes: [
    'reportingTeams',
    'reportingPlayers',
    'reportingSessions',
    'sessionSummary',
    'playerSummary',
    'playerActions'
  ],
  endpoints(build) {
    return {}
  }
})
